import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const SEV_COLOR = { LOW: "#7AC860", MEDIUM: "#F5C842", HIGH: "#E8A020", EXTREME: "#C84A3A" };

export default function AdminIngestion() {
  const [feeds, setFeeds] = useState([]);
  const [runs, setRuns] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState("");
  const [feedFilter, setFeedFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [f, r, i] = await Promise.all([
        adminApi.ingestionFeeds(),
        adminApi.ingestionRuns(25),
        adminApi.ingestionItems(60, feedFilter || null),
      ]);
      setFeeds(f.data?.feeds || []);
      setRuns(r.data?.runs || []);
      setItems(i.data?.items || []);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [feedFilter]);

  const runNow = async () => {
    setRunning(true);
    setToast("Pulling RSS feeds… may take 10-30s");
    try {
      const { data } = await adminApi.ingestionRun();
      setToast(`Run complete · fetched ${data.fetched_total} · new ${data.new_signals_total}`);
      await load();
    } catch (e) {
      const msg = e?.response?.status === 429 ? "Rate limit exceeded" : "Run failed";
      setToast(msg);
    } finally {
      setRunning(false);
      setTimeout(() => setToast(""), 8000);
    }
  };

  const lastRun = runs[0];

  return (
    <div className="admin-page mesh-shell" data-testid="admin-ingestion">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />
      <div className="admin-page__head">
        <h2 className="admin-page__title">Signal Ingestion</h2>
        <div className="mesh-eyebrow">v14 · Real-time RSS connectors → SignalFabric</div>
      </div>

      {toast && <div className="mesh-toast" data-testid="ingestion-toast">{toast}</div>}

      <section className="admin-section mesh-section" data-testid="ingestion-controls">
        <h3 className="admin-section__title">Run Control</h3>
        <div className="admin-filters">
          <button type="button" className="admin-linkbtn" disabled={running} onClick={runNow} data-testid="ingestion-run-btn">
            {running ? "Running…" : "▶ Run Ingestion Now"}
          </button>
          <button type="button" className="admin-linkbtn" onClick={load} data-testid="ingestion-refresh">Refresh</button>
          <span className="admin-foot-note" style={{ marginLeft: 12 }}>
            {lastRun ? `Last run: ${new Date(lastRun.started_at).toLocaleString()} · fetched ${lastRun.fetched_total} · new ${lastRun.new_signals_total}` : "Never run"}
          </span>
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="ingestion-feeds">
        <h3 className="admin-section__title">Configured Feeds ({feeds.length})</h3>
        <div className="mesh-service-grid">
          {feeds.map((f) => (
            <article key={f.key} className="mesh-service-card" data-testid={`ingestion-feed-${f.key}`}>
              <header className="mesh-service-card__head">
                <span className="mesh-pill mesh-pill--ghost">{f.watchtower}</span>
                <span className="mesh-pill mesh-pill--ghost">{f.category}</span>
              </header>
              <h3 className="mesh-service-card__title">{f.name}</h3>
              <p className="mesh-service-card__sum"><code style={{ fontSize: 11 }}>{f.key}</code></p>
              <div className="admin-filters">
                <button type="button" className="admin-linkbtn" onClick={() => setFeedFilter(f.key)} data-testid={`ingestion-filter-${f.key}`}>View items</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="ingestion-runs">
        <h3 className="admin-section__title">Recent Runs</h3>
        <table className="admin-table">
          <thead><tr><th>Started</th><th>Finished</th><th>Feeds</th><th>Fetched</th><th>New Signals</th></tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id || r._id} data-testid={`run-${(r.id||r._id).slice(0,8)}`}>
                <td>{r.started_at?.slice(0, 19).replace("T", " ")}</td>
                <td>{r.finished_at?.slice(0, 19).replace("T", " ")}</td>
                <td>{(r.feeds || []).length}</td>
                <td>{r.fetched_total}</td>
                <td><strong>{r.new_signals_total}</strong></td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={5} className="admin-foot-note">No runs yet.</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="admin-section mesh-section" data-testid="ingestion-items">
        <h3 className="admin-section__title">Recent Items {feedFilter && `· ${feedFilter}`}</h3>
        <div className="admin-filters" style={{ marginBottom: 8 }}>
          {feedFilter && <button type="button" className="admin-linkbtn" onClick={() => setFeedFilter("")} data-testid="ingestion-clear-filter">Clear filter</button>}
        </div>
        <ol className="mesh-timeline">
          {items.map((it) => (
            <li key={it.id || it._id} className="mesh-timeline__item" style={{ borderColor: SEV_COLOR[it.severity] || "#7AC860" }}>
              <span className="mesh-timeline__time">{it.created_at?.slice(11, 16)}</span>
              <span className="mesh-pill" style={{ borderColor: SEV_COLOR[it.severity], color: SEV_COLOR[it.severity] }}>{it.severity}</span>
              <span className="mesh-pill mesh-pill--ghost">{it.feed_name || it.feed_key}</span>
              <span className="mesh-pill mesh-pill--ghost">{it.watchtower}</span>
              <a href={it.link} target="_blank" rel="noopener noreferrer" className="mesh-timeline__title" style={{ color: "inherit", textDecoration: "none" }}>{it.title}</a>
            </li>
          ))}
          {items.length === 0 && <div className="admin-foot-note">No items yet — run ingestion above.</div>}
        </ol>
      </section>
    </div>
  );
}
